import ScrollTrigger from "gsap/ScrollTrigger";

const refreshScrollTrigger = () => {
	requestAnimationFrame(() => {
		ScrollTrigger.refresh();
	});
};

const toggleProjectFaqsState = (isActive) => {
	const faqs = document.querySelectorAll(".home-faqs-section.project-faqs");

	faqs.forEach((section) => {
		section.classList.toggle("has-project-filter-active", isActive);
	});
};

const getElements = () => {
	const wrapper = document.querySelector(".project-showcase");

	if (!wrapper) {
		return null;
	}

	return {
		wrapper,
		tabs: wrapper.querySelectorAll(".project-showcase__tab"),
		panel: wrapper.querySelector(".project-showcase__panel"),
		list: wrapper.querySelector(".project-showcase__list"),
		loadMore: wrapper.querySelector(".project-showcase__button--next"),
		loadPrev: wrapper.querySelector(".project-showcase__button--prev"),
		title: wrapper.querySelector(".project-showcase__title"),
		subtitle: wrapper.querySelector(".project-showcase__subtitle"),
	};
};

const getAjaxConfig = () => window.ajax_object || {};

const getProjectIcon = (wrapper, icon, iconDark) => {
	if (wrapper?.classList.contains("has-active-filter")) {
		return iconDark || icon || "";
	}

	return icon || "";
};

const updatePreview = (panel, item) => {
	if (!panel || !item) {
		return;
	}

	const image = panel.querySelector(".project-showcase__image");
	const imageUrl = panel.querySelector(".project-showcase__preview-inner");
	const excerpt = panel.querySelector(".project-showcase__excerpt");
	const iconContainer = panel.querySelector(
		".project-showcase__project-icon",
	);

	if (image) {
		image.src = item.dataset.image || "";
		image.alt = item.dataset.title || "";
	}

	if (imageUrl) {
		imageUrl.href = item.dataset.link;
		imageUrl.setAttribute(
			"aria-label",
			`Ver proyecto: ${item.dataset.title || ""}`,
		);
	}

	if (excerpt) {
		excerpt.innerHTML = item.dataset.excerpt || "";
	}

	if (iconContainer) {
		iconContainer.innerHTML = "";
		const icon = getProjectIcon(
			panel.closest(".project-showcase"),
			item.dataset.icon,
			item.dataset.iconDark,
		);

		if (icon) {
			const img = document.createElement("img");

			img.src = icon;
			img.alt = "project icon";

			iconContainer.appendChild(img);
		}
	}
};

const updatePreviewFromData = (panel, preview) => {
	if (!panel) {
		return;
	}

	const previewData = preview || {};
	const image = panel.querySelector(".project-showcase__image");
	const imageUrl = panel.querySelector(".project-showcase__preview-inner");
	const excerpt = panel.querySelector(".project-showcase__excerpt");
	const iconContainer = panel.querySelector(
		".project-showcase__project-icon",
	);

	if (image) {
		image.src = previewData.image || "";
		image.alt = previewData.title || "";
	}

	if (imageUrl) {
		imageUrl.href = previewData.link || "#";
		imageUrl.setAttribute(
			"aria-label",
			`Ver proyecto: ${previewData.title || ""}`,
		);
	}

	if (excerpt) {
		excerpt.innerHTML = previewData.excerpt || "";
	}

	if (iconContainer) {
		iconContainer.innerHTML = "";
		const icon = getProjectIcon(
			panel.closest(".project-showcase"),
			previewData.icon,
			previewData.icon_dark,
		);

		if (icon) {
			const img = document.createElement("img");

			img.src = icon;
			img.alt = "project icon";

			iconContainer.appendChild(img);
		}
	}
};

const moveActiveElements = (panel, item) => {
	if (!panel || !item) {
		return;
	}

	const previewLink = panel.querySelector(".project-showcase__preview-inner");

	const top = item.offsetTop;

	if (previewLink) {
		if (window.innerWidth <= 576) {
			previewLink.style.top = "50%";
		} else if (window.innerWidth <= 991) {
			previewLink.style.top = "60%";
		} else {
			previewLink.style.top = `${top}px`;
		}
	}
};

const activateItem = (panel, item) => {
	const items = panel.querySelectorAll(".project-showcase__item");

	items.forEach((current) => {
		current.classList.remove("is-active");
	});

	item.classList.add("is-active");

	moveActiveElements(panel, item);

	updatePreview(panel, item);
};

const updateListRows = (list) => {
	if (!list) {
		return;
	}

	list.style.setProperty(
		"--items",
		list.querySelectorAll(".project-showcase__item").length || 4,
	);
};

const shouldUseTwoTapNavigation = () =>
	window.matchMedia("(hover: none), (pointer: coarse)").matches;

const bindItems = (panel) => {
	const items = panel.querySelectorAll(".project-showcase__item");

	items.forEach((item) => {
		if (item.dataset.projectShowcaseBound) {
			return;
		}

		const handler = () => {
			activateItem(panel, item);
		};
		const link = item.querySelector("a");

		item.addEventListener("mouseenter", handler);
		item.addEventListener("focusin", handler);

		if (link) {
			link.addEventListener("click", (event) => {
				if (!shouldUseTwoTapNavigation()) {
					return;
				}

				if (item.dataset.touchNavigationArmed === "true") {
					return;
				}

				event.preventDefault();

				items.forEach((current) => {
					delete current.dataset.touchNavigationArmed;
				});

				activateItem(panel, item);
				item.dataset.touchNavigationArmed = "true";
			});
		}

		item.dataset.projectShowcaseBound = "true";
	});

	updateListRows(panel.querySelector(".project-showcase__list"));

	if (items.length) {
		const initialItem =
			panel.querySelector(".project-showcase__item.is-active") ||
			items[0];

		activateItem(panel, initialItem);
		initialItem.dataset.touchNavigationArmed = "true";
	}
};

const setLoading = (elements, isLoading) => {
	elements.wrapper.classList.toggle("is-loading", isLoading);

	if (elements.loadMore) {
		elements.loadMore.disabled = isLoading;
	}

	if (elements.loadPrev) {
		elements.loadPrev.disabled = isLoading;
	}

	elements.tabs.forEach((tab) => {
		tab.disabled = isLoading;
	});
};

const updatePaginationControls = (elements) => {
	const currentPage = parseInt(elements.wrapper.dataset.page || "1", 10);
	const hasMore = elements.wrapper.dataset.hasMore === "true";

	if (elements.loadPrev) {
		elements.loadPrev.classList.toggle("hidden", currentPage <= 1);
	}

	if (elements.loadMore) {
		elements.loadMore.classList.toggle("hidden", !hasMore);
	}
};

const requestProjects = async (elements, { termId, page }) => {
	const ajaxConfig = getAjaxConfig();

	if (!ajaxConfig.ajax_url) {
		return null;
	}

	const body = new URLSearchParams();

	body.append("action", "load_project_showcase");
	body.append("page", page);
	body.append("term_id", termId || "");

	const response = await fetch(ajaxConfig.ajax_url, {
		method: "POST",
		credentials: "same-origin",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
		},
		body,
	});

	if (!response.ok) {
		return null;
	}

	const data = await response.json();

	return data.success ? data.data : null;
};

const renderProjects = (elements, data) => {
	if (!data || !elements.list) {
		return;
	}

	elements.list.innerHTML = data.html || "";
	updatePreviewFromData(elements.panel, data.preview);

	elements.wrapper.dataset.hasMore = data.has_more ? "true" : "false";

	bindItems(elements.panel);
	updatePaginationControls(elements);
};

const unbind = (elements) => {
	elements.tabs.forEach((tab) => {
		tab.removeEventListener("click", async () => {});
	});
};

const bindTabs = (elements) => {
	unbind(elements);
	elements.tabs.forEach((tab) => {
		tab.addEventListener("click", async () => {
			const isActive = tab.classList.contains("is-active");
			const termId = isActive ? "" : tab.dataset.termId || "";

			setLoading(elements, true);

			try {
				const data = await requestProjects(elements, {
					termId,
					page: 1,
				});

				if (data) {
					elements.wrapper.dataset.page = "1";
					elements.wrapper.dataset.termId = termId;
					elements.wrapper.dataset.hasMore = data.has_more
						? "true"
						: "false";
					elements.wrapper.classList.toggle(
						"has-active-filter",
						Boolean(termId),
					);
					toggleProjectFaqsState(Boolean(termId));

					elements.tabs.forEach((current) => {
						current.classList.toggle(
							"is-active",
							!isActive && current === tab,
						);
					});

					if (elements.title) {
						elements.title.textContent = isActive
							? elements.wrapper.dataset.defaultTitle || ""
							: tab.dataset.title ||
								elements.wrapper.dataset.defaultTitle ||
								"";
					}

					if (elements.subtitle) {
						elements.subtitle.textContent = isActive
							? elements.wrapper.dataset.defaultSubtitle || ""
							: tab.dataset.subtitle ||
								elements.wrapper.dataset.defaultSubtitle ||
								"";
					}

					renderProjects(elements, data);
					refreshScrollTrigger();
				}
			} finally {
				setLoading(elements, false);
			}
		});
	});
};

const bindLoadMore = (elements) => {
	if (!elements.loadMore) {
		return;
	}

	elements.loadMore.addEventListener("click", async () => {
		if (elements.wrapper.classList.contains("is-loading")) {
			return;
		}

		const currentPage = parseInt(elements.wrapper.dataset.page || "1", 10);
		const nextPage = currentPage + 1;
		const termId = elements.wrapper.dataset.termId || "";

		setLoading(elements, true);

		try {
			const data = await requestProjects(elements, {
				termId,
				page: nextPage,
			});

			if (data) {
				elements.wrapper.dataset.page = String(nextPage);
				renderProjects(elements, data);
			}
		} finally {
			setLoading(elements, false);
		}
	});
};

const bindLoadPrev = (elements) => {
	if (!elements.loadPrev) {
		return;
	}

	elements.loadPrev.addEventListener("click", async () => {
		if (elements.wrapper.classList.contains("is-loading")) {
			return;
		}

		const currentPage = parseInt(elements.wrapper.dataset.page || "1", 10);
		const prevPage = currentPage - 1;

		if (prevPage < 1) {
			return;
		}

		const termId = elements.wrapper.dataset.termId || "";

		setLoading(elements, true);

		try {
			const data = await requestProjects(elements, {
				termId,
				page: prevPage,
			});

			if (data) {
				elements.wrapper.dataset.page = String(prevPage);
				renderProjects(elements, data);
			}
		} finally {
			setLoading(elements, false);
		}
	});
};

const projectShowcase = () => {
	if (
		window.innerWidth < 992 &&
		!document
			.querySelector("body")
			.classList.contains("page-template-page-featured-projects")
	) {
		return;
	}

	const elements = getElements();

	if (!elements) {
		return;
	}

	if (elements.wrapper.dataset.projectShowcaseInitialized) {
		return;
	}

	elements.wrapper.dataset.projectShowcaseInitialized = "true";

	if (elements.panel) {
		bindItems(elements.panel);
	}

	bindTabs(elements);
	bindLoadMore(elements);
	bindLoadPrev(elements);
	updatePaginationControls(elements);
	toggleProjectFaqsState(Boolean(elements.wrapper.dataset.termId));
};

export { projectShowcase };
